import { FormControl, FormLabel, RadioGroup, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { CartModifierGroup } from '../../../utils/cart';
import {
  checkItemInCartItemTree,
  isDefaultItemInModGroup,
  isItem86edToday,
} from '../../../utils/menu';
import { ModGroupLabel } from '../components/ModGroupLabel';
import RecursiveModGroup from '../menu/RecursiveModGroup';
import SingularModChoice from '../components/SingularModChoice';
import {
  getCartItemDescription,
  getModGroupItemLabel,
  sortAndFilterMenuItems,
} from '../util/helper';
import { IModGroup } from './modGroup.props';
import {
  canShowItem,
  hasModGroupChildren,
  EXPRESS_SUPPRESSED_LABEL_TOOLTIP,
} from './modGroup.utils';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import Colors from '../../../utils/color';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import { useSelector } from 'react-redux';
import {
  selectIsExpressUpsellSuppressionActive,
  selectUnavailableItemsByName,
} from '../../../selectors/menu';
import { isExpressSuppressedUpsellPrompt } from '../../../utils/cart.upsellConditions';

const useStyles = makeStyles<{
  hasChildren: boolean;
  isHighlighted: boolean;
  isLabelClickable: boolean;
  isLabelSuppressed: boolean;
}>()(
  (
    { spacing },
    { hasChildren, isHighlighted, isLabelClickable, isLabelSuppressed }
  ) => {
    const bgColor = isHighlighted ? Colors.alto : Colors.white;
    const mBottom = hasChildren ? spacing(1.25) : 0;
    // Express mode suppressed this heading: not-allowed rather than the default
    // arrow, so the label reads as deliberately inert instead of unresponsive.
    let labelCursor = 'default';
    if (isLabelClickable) {
      labelCursor = 'pointer';
    } else if (isLabelSuppressed) {
      labelCursor = 'not-allowed';
    }
    return {
      modGroup: {
        margin: '0 !important',
        '& legend': {
          width: '100%',
        },
        width: '100%',
        backgroundColor: bgColor,
      },
      modGroupContainer: {
        marginBottom: mBottom,
      },
      modGroupLabel: {
        cursor: labelCursor,
      },
    };
  }
);

export default function SingularSelectionModGroup(props: IModGroup) {
  const {
    modGroup,
    onChange,
    cartItem,
    pathToItem,
    parentModifier,
    isHighlighted,
    isSelectedToFocus,
    sendModGroupTTSMessage,
    sendTTSMessage,
  } = props;

  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  let currentValue: string | null = null;
  let selectedModNumber = 0;
  const currentItem = checkItemInCartItemTree({
    cartItem,
    pathToItem,
  }) as CartModifierGroup;
  if (currentItem) {
    const selectedMod = currentItem?.selectedItems || [];
    currentValue = Object.values(selectedMod)[0]?.itemId || null;
    selectedModNumber = Object.values(selectedMod).length;
  }

  const isBrandMenu = getMenuUnificationFeatureFlag();

  // sort and filter the modifiers before rendering
  const menuItems = isBrandMenu
    ? Object.values(modGroup.menuItems)
    : sortAndFilterMenuItems(modGroup.menuItems);

  const key = [cartItem.id, modGroup.id].join('_');
  const hasChildren = hasModGroupChildren(props);
  const hasSpokenDescription = !!(isBrandMenu && modGroup.description);
  // Express mode must never ask a `do_not_upsell_in_express_mode`-tagged
  // upsell prompt, so stop advertising its heading as clickable (HITL-1529).
  // Mirrors the
  // rule enforced in useTTSMessage; a boolean selector re-renders the group
  // only when express mode itself flips, which happens at most once an order.
  // Scoped to headings that would otherwise have spoken: a group with nothing
  // to say was never clickable, so it has no suppression to explain.
  const isExpressUpsellSuppressed = useSelector(
    selectIsExpressUpsellSuppressionActive
  );
  const isLabelSuppressed =
    hasSpokenDescription &&
    isExpressSuppressedUpsellPrompt(modGroup, isExpressUpsellSuppressed);
  const isLabelClickable = hasSpokenDescription && !isLabelSuppressed;

  const { classes } = useStyles({
    hasChildren,
    isHighlighted: !!isHighlighted || !!isSelectedToFocus,
    isLabelClickable,
    isLabelSuppressed,
  });

  // Gated on the description alone, not on isLabelClickable: when express mode
  // has suppressed the prompt the click must still reach the hook, which drops
  // the TTS and records the attempt as a metric.
  const handleLabelClick = () => {
    if (hasSpokenDescription) {
      sendModGroupTTSMessage(modGroup);
    }
  };

  const modGroupHeading = (
    <div
      onClick={handleLabelClick}
      // The wrapper is a full-width block, so a tooltip anchored to it centres
      // on the whole row and drops over the option chips. Shrink the anchor to
      // the heading text while it is suppressed — the click target narrows with
      // it, which is fine here: the click is already a no-op that only records
      // the attempt, and a blank stretch of row should not record one.
      style={isLabelSuppressed ? { width: 'fit-content' } : undefined}
    >
      <FormLabel className={classes.modGroupLabel}>
        <ModGroupLabel
          modGroup={modGroup}
          selectedModNumber={selectedModNumber}
          parentModifier={parentModifier}
        />
      </FormLabel>
    </div>
  );

  return (
    <div key={key} className={classes.modGroupContainer}>
      <FormControl
        component="fieldset"
        className={classes.modGroup}
        data-testid="mod-group-form"
      >
        {isLabelSuppressed ? (
          <Tooltip
            title={EXPRESS_SUPPRESSED_LABEL_TOOLTIP}
            arrow
            placement="right"
          >
            {modGroupHeading}
          </Tooltip>
        ) : (
          modGroupHeading
        )}
        <RadioGroup
          row
          aria-label={modGroup.name}
          name={modGroup.name}
          value={currentValue}
          onChange={onChange(modGroup)}
        >
          {menuItems.map((child) => {
            const is86edToday = isItem86edToday(child);
            const pathToModChoice = [pathToItem, child.id].join('__');
            const isSelected = !!checkItemInCartItemTree({
              cartItem,
              pathToItem: pathToModChoice,
            });
            const id = child.itemId;
            const name = child.name;
            const modLabel = getModGroupItemLabel(child);

            const isDefaultItem = isDefaultItemInModGroup(id, modGroup);

            const isItemUnavailableNow = isBrandMenu
              ? isBMModifierAnUnavailableConstraint(
                  cartItem,
                  unavailableItemsByName,
                  parentModifier,
                  pathToItem,
                  modGroup.name,
                  child.name
                )
              : false;

            const showItem = canShowItem({
              isBrandMenu,
              unavailableItemsByName,
              itemName: name,
              cartItem,
              modGroupName: modGroup.name,
              childAvailability: child?.available,
              is86edToday,
            });

            if (showItem) {
              const childDescription = getCartItemDescription(
                child,
                isBrandMenu
              );
              const onInfoClick = childDescription
                ? () => sendTTSMessage(childDescription)
                : undefined;
              return (
                <SingularModChoice
                  key={pathToModChoice}
                  id={id}
                  name={name}
                  modLabel={modLabel}
                  is86edToday={
                    isBrandMenu
                      ? !child.available || is86edToday || isItemUnavailableNow
                      : is86edToday
                  }
                  isSelected={isSelected}
                  isDefault={isDefaultItem}
                  onInfoClick={onInfoClick}
                  description={childDescription}
                />
              );
            }
            return null;
          })}
        </RadioGroup>
      </FormControl>
      <RecursiveModGroup {...props} />
    </div>
  );
}
